"use client";

import { useState } from "react";
import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphProductVariantsSelector as ParagraphProductVariantsSelectorData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { ProductScreenFrame } from "./ProductScreenFrame";
import styles from "./ParagraphProductVariantsSelector.module.css";

/**
 * Layout 54 - `PARAGRAPH_PRODUCT_VARIANTS_SELECTOR_LAYOUT`: interactive product variants.
 *
 * Client component z stanem active variant. Kliknicie karty pokazuje 2-kolumnowy
 * content (ekrany wariantu + tresc + features + CTA). Pulsujaca ikona reki nad kartami
 * + bouncing strzalka.
 *
 * Ekrany wariantu (`variants[].screens`) w ramce przeglądarki - ProductScreenFrame jak w "Hero product"
 * (układ 50): rotacja kilku zrzutów, opcjonalne przewijanie długiego zrzutu, preloader. Ustawienia
 * ramki są wspólne dla wariantów; zmiana wariantu podmienia listę ekranów i wraca do pierwszego.
 */
export function ParagraphProductVariantsSelector({
  paragraph,
  t: _t,
}: {
  paragraph: ParagraphProductVariantsSelectorData;
  t: ParagraphTranslations;
}) {
  const {
    name: title,
    text: desc,
    hint_pill: hintPill,
    variants,
    browser_url,
    image_scroll,
    image_scroll_speed,
    image_scroll_pause,
    screen_interval,
  } = paragraph;
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  //Karty widoczne (bez pustych - filtrujemy po name)
  const availableVariants = variants
    .map((v, idx) => ({ v, idx }))
    .filter(({ v }) => v.name.trim() !== "");

  if (availableVariants.length === 0) return null;

  const active = activeIdx !== null ? variants[activeIdx] : null;
  const activeScreens = active?.screens ?? [];
  const activeBtnInternal =
    active && active.btn_url ? extractInternalHref(active.btn_url) : null;

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {desc && desc.trim() !== "" ? (
          <div className={styles.desc} dangerouslySetInnerHTML={{ __html: desc }} />
        ) : null}

        {hintPill ? (
          <>
            <div className={styles.hintPill}>
              <span className={styles.handIcon} aria-hidden="true">
                <i className="nc-icon-outline media-1_touch" />
              </span>
              <span>{hintPill}</span>
            </div>
            <div className={styles.arrowDown} aria-hidden="true">
              <i className="nc-icon-outline arrows-1_tail-down" />
            </div>
          </>
        ) : null}

        <ul className={styles.cards} role="tablist">
          {availableVariants.map(({ v, idx }) => (
            <li key={idx}>
              <button
                type="button"
                role="tab"
                aria-selected={activeIdx === idx}
                className={`${styles.card} ${activeIdx === idx ? styles.cardActive : ""}`}
                onClick={() => setActiveIdx(idx)}
              >
                <span className={styles.cardName}>{v.name}</span>
                {v.subtitle ? (
                  <span className={styles.cardSubtitle}>{v.subtitle}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>

        {active === null ? (
          <div className={styles.placeholder}>
            <span className={styles.placeholderIcon} aria-hidden="true">
              <i className="nc-icon-outline ui-1_check-square-09" />
            </span>
            <h3 className={styles.placeholderTitle}>Wybierz wariant powyżej</h3>
            <p className={styles.placeholderDesc}>
              Kliknij jeden z wariantów, aby zobaczyć jego funkcje, ekran systemu i pełny opis.
            </p>
          </div>
        ) : (
          <div className={styles.content}>
            {activeScreens.length > 0 ? (
              <div className={styles.mediaWrap}>
                {/* key: nowy wariant = świeża ramka (animacje i preloader od pierwszego ekranu). */}
                <ProductScreenFrame
                  key={activeIdx}
                  images={activeScreens}
                  browserUrl={browser_url ?? ""}
                  scroll={image_scroll === true}
                  speed={image_scroll_speed ?? 80}
                  pauseSeconds={image_scroll_pause ?? 2}
                  intervalSeconds={screen_interval ?? 5}
                />
              </div>
            ) : (
              <div className={styles.mediaPlaceholder} aria-hidden="true" />
            )}

            <div className={styles.body}>
              {active.eyebrow ? (
                <span className={styles.eyebrow}>{active.eyebrow}</span>
              ) : null}
              {active.name ? <h3 className={styles.contentTitle}>{active.name}</h3> : null}
              {active.desc_lead ? (
                <p className={styles.descLead}>{active.desc_lead}</p>
              ) : null}
              {active.desc && active.desc.trim() !== "" ? (
                <div
                  className={styles.descBody}
                  dangerouslySetInnerHTML={{ __html: active.desc }}
                />
              ) : null}

              {active.features.length > 0 ? (
                <ul className={styles.features}>
                  {active.features.map((ft, i) => (
                    <li key={i} className={styles.feature}>
                      <i
                        className={`${styles.featureCheck} nc-icon-outline ui-1_check`}
                        aria-hidden="true"
                      />
                      <span>{ft}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {active.btn_label && active.btn_url ? (
                activeBtnInternal !== null ? (
                  <Link href={activeBtnInternal} className={styles.ctaBtn}>
                    {active.btn_label}
                  </Link>
                ) : (
                  <a href={active.btn_url} className={styles.ctaBtn}>
                    {active.btn_label}
                  </a>
                )
              ) : null}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
